import React, { useEffect, useState } from "react";
import "./TrashModal.css";
import { backdropDismiss } from "../../../../utils/backdropDismiss";
import { 
  fetchMasterScheduleSessions, 
  fetchScheduledSessions,
  restoreMasterScheduleSession,
  restoreScheduledSession,
  permanentlyDeleteScheduledSession,
  permanentlyDeleteMasterSession,
  batchPermanentlyDeleteScheduledSessions,
  batchPermanentlyDeleteMasterSessions,
  fetchTrashSessions,
} from "../schedulingApi";

// Simple overlap helper
function checkOverlap(startA, endA, startB, endB) {
  return startA < endB && startB < endA;
}

// 14 Days in Milliseconds
const RETENTION_MS = 14 * 24 * 60 * 60 * 1000; 

export default function TrashModal({
  isOpen,
  onClose,
  isMasterMode,
  selectedTutorUid,
  weekStart, 
  weekEnd,
  onRestoreSuccess 
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [localRefresh, setLocalRefresh] = useState(0);

  // 1. Fetch Deleted Sessions & Run Retention Policy
  useEffect(() => {
    if (!isOpen) return;

    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        let rows = [];
        if (isMasterMode) {
          const all = await fetchMasterScheduleSessions({
            tutorUid: selectedTutorUid,
            includeDeleted: true
          });
          rows = all.filter(s => !!s.isDeleted);
        } else {
          // GLOBAL TRASH: Fetch all recent deleted items regardless of date/view
          let allTrash = await fetchTrashSessions();

          // Optional: If a specific tutor is selected in the main view, 
          // filter the global trash to show only that tutor's deleted items.
          if (selectedTutorUid) {
            allTrash = allTrash.filter(s => s.tutorUid === selectedTutorUid);
          }

          rows = allTrash;
        }

        if (!mounted) return;

        // --- RETENTION POLICY CHECK ---
        const now = Date.now();
        const validItems = [];
        const expiredIds = [];

        rows.forEach(r => {
          // If deletedAt is missing, assume it's recent (or very old? Safe to keep for now)
          if (!r.deletedAt) {
            validItems.push(r);
            return;
          }
          const dDate = r.deletedAt.toDate ? r.deletedAt.toDate() : new Date(r.deletedAt);
          const diff = now - dDate.getTime();
          
          if (diff > RETENTION_MS) {
            expiredIds.push(r.id);
          } else {
            validItems.push(r);
          }
        });

        // Fire-and-forget auto-purge of expired items
        if (expiredIds.length > 0) {
          console.log(`Auto-purging ${expiredIds.length} expired items from trash.`);
          if (isMasterMode) {
            batchPermanentlyDeleteMasterSessions(expiredIds).catch(e => console.error("Auto-purge failed", e));
          } else {
            batchPermanentlyDeleteScheduledSessions(expiredIds).catch(e => console.error("Auto-purge failed", e));
          }
        }

        // Sort remaining valid items
        validItems.sort((a, b) => {
           const dA = a.deletedAt?.toDate ? a.deletedAt.toDate() : new Date(0);
           const dB = b.deletedAt?.toDate ? b.deletedAt.toDate() : new Date(0);
           return dB - dA;
        });

        setItems(validItems);

      } catch (err) {
        console.error("Error loading trash:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [isOpen, isMasterMode, selectedTutorUid, weekStart, weekEnd, localRefresh]);

  // 2. Conflict Check & Restore Logic
  const handleRestore = async (session) => {
    if (!session || !session.start || !session.end) return;

    const sessionStart = session.start instanceof Date ? session.start : session.start.toDate();
    const sessionEnd = session.end instanceof Date ? session.end : session.end.toDate();

    try {
      setLoading(true);

      // A. Fetch ACTIVE sessions
      let activeSessions = [];
      if (isMasterMode) {
        const all = await fetchMasterScheduleSessions({ 
          tutorUid: session.tutorUid, 
          includeDeleted: false 
        });
        activeSessions = all;
      } else {
        const dayStart = new Date(sessionStart);
        dayStart.setHours(0,0,0,0);
        const dayEnd = new Date(dayStart);
        dayEnd.setDate(dayEnd.getDate() + 1);

        activeSessions = await fetchScheduledSessions({
          rangeStart: dayStart,
          rangeEnd: dayEnd,
          tutorUid: session.tutorUid,
          includeDeleted: false
        });
      }

      // B. Check for Overlap AND Seat Conflict
      const hasConflict = activeSessions.some(active => {
        // Ignore self
        if (active.id === session.id) return false;

        // --- FIX: Ignore Canceled Sessions ---
        const status = (active.status || "Pending").toLowerCase();
        if (status === "cancelled" || status === "canceled") return false; 
        
        // 1. Time Overlap Check
        const aStart = active.start instanceof Date ? active.start : active.start.toDate();
        const aEnd = active.end instanceof Date ? active.end : active.end.toDate();
        const overlaps = checkOverlap(sessionStart, sessionEnd, aStart, aEnd);
        
        if (!overlaps) return false;

        // 2. Seat (SlotIdx) Check
        // If seat indices match, it's a conflict.
        const activeIdx = active.slotIdx ?? 0;
        const sessionIdx = session.slotIdx ?? 0;
        
        return activeIdx === sessionIdx;
      });

      if (hasConflict) {
        alert("Cannot restore: This specific seat/slot is already occupied by an active session.\nPlease clear the seat manually before restoring.");
        setLoading(false);
        return;
      }

      // C. Perform Restore
      if (isMasterMode) {
        await restoreMasterScheduleSession(session.id);
      } else {
        await restoreScheduledSession(session.id);
      }

      const restoredPayload = {
        ...session,
        start: sessionStart,
        end: sessionEnd,
      };
      await onRestoreSuccess?.(restoredPayload); 
      setLocalRefresh(n => n + 1); 

    } catch (err) {
      console.error("Restore failed:", err);
      alert("Failed to restore session.");
    } finally {
      setLoading(false);
    }
  };

  // 3. Manual Permanent Delete (Single)
  const handleDeleteForever = async (session) => {
    if(!window.confirm("Permanently delete this session? This cannot be undone.")) return;
    
    try {
      setLoading(true);
      if (isMasterMode) {
        await permanentlyDeleteMasterSession(session.id);
      } else {
        await permanentlyDeleteScheduledSession(session.id);
      }
      setLocalRefresh(n => n + 1);
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete session.");
      setLoading(false);
    }
  };

  // 4. Empty Trash (Batch)
  const handleEmptyTrash = async () => {
    if (items.length === 0) return;
    if (!window.confirm(`Permanently delete all ${items.length} sessions in the trash? This cannot be undone.`)) return;

    try {
      setLoading(true);
      const ids = items.map(i => i.id);
      
      if (isMasterMode) {
        await batchPermanentlyDeleteMasterSessions(ids);
      } else {
        await batchPermanentlyDeleteScheduledSessions(ids);
      }
      setLocalRefresh(n => n + 1);
    } catch (err) {
      console.error("Empty trash failed:", err);
      alert("Failed to empty trash.");
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="trashModalOverlay" {...backdropDismiss(onClose)}>
      <div className="trashModalCard" onClick={e => e.stopPropagation()}>
        
        <div className="trashHeader">
          <div className="trashTitle">
            Deleted Sessions 
            <span className="trashCountBadge">{items.length}</span>
          </div>
          
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {items.length > 0 && (
              <button 
                className="trashEmptyBtn" 
                onClick={handleEmptyTrash}
                disabled={loading}
              >
                Empty Trash
              </button>
            )}
            <button className="trashCloseBtn" onClick={onClose} title="Close">
              &times;
            </button>
          </div>
        </div>

        <div className="trashToolbar">
          <span style={{ fontSize: "13px", color: "#6b7280" }}>
            {isMasterMode ? "Master Schedule Trash" : "Weekly Schedule Trash"}
          </span>
          <span style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic", marginLeft: "auto", marginRight: "10px" }}>
            Items older than 14 days are auto-deleted
          </span>
          <button className="trashRefreshLink" onClick={() => setLocalRefresh(n => n + 1)}>
            Refresh
          </button>
        </div>

        <div className="trashBody">
          {loading && items.length === 0 && (
            <div className="trashLoading">Loading deleted sessions...</div>
          )}
          
          {!loading && items.length === 0 && (
            <div className="trashEmpty">
              No deleted sessions found.<br/>
              <span style={{ fontSize: "13px", opacity: 0.7 }}>
                (Only sessions deleted from {isMasterMode ? "the Master Schedule" : "this week"} appear here)
              </span>
            </div>
          )}

          <div className="trashList">
            {items.map(s => {
              const start = s.start instanceof Date ? s.start : s.start.toDate();
              const end = s.end instanceof Date ? s.end : s.end.toDate();
              
              const dateStr = start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
              const timeStr = `${start.toLocaleTimeString([], {hour:'numeric', minute:'2-digit'})} - ${end.toLocaleTimeString([], {hour:'numeric', minute:'2-digit'})}`;
              const deletedAt = s.deletedAt?.toDate ? s.deletedAt.toDate().toLocaleString() : "Unknown";

              return (
                <div key={s.id} className="trashRow">
                  <div className="trashRowMain">
                    <div className="trashRowTitle">
                      {s.studentName || "Unknown Student"} 
                      <span style={{ fontWeight: 400, color: "#9ca3af" }}> — {s.subject || "No Subject"}</span>
                    </div>
                    <div className="trashRowMeta">
                      {s.tutorName || "Unknown Tutor"} • Seat {s.slotIdx !== undefined ? s.slotIdx + 1 : "?"}
                    </div>
                    <div className="trashRowTime">
                      {/* Line 1: Session Time */}
                      <div className="trashSessionTime">
                        {dateStr} • {timeStr}
                      </div>
                      
                      {/* Line 2: Deletion Metadata */}
                      <div className="trashDeletedMeta" title={`Deleted at ${deletedAt}`}>
                        Deleted {deletedAt}
                        {s.deletedByName && (
                          <span className="trashDeletedBy"> by <strong>{s.deletedByName}</strong></span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="trashActions">
                    <button 
                      className="trashDeleteForeverBtn"
                      onClick={() => handleDeleteForever(s)}
                      disabled={loading}
                      title="Delete Forever"
                    >
                      Delete
                    </button>
                    <button 
                      className="trashRestoreBtn"
                      onClick={() => handleRestore(s)}
                      disabled={loading}
                    >
                      Restore
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
}
